/**
 * Cart Drawer
 * Clean, performant cart drawer with smooth animations.
 * Fully isolated - overrides default sidebar behavior for cart only.
 */

(function() {
  'use strict';

  // ============================================================
  // Drawer Controller
  // ============================================================

  const Drawer = {
    el: null,
    overlay: null,
    isOpen: false,

    init() {
      this.el = document.getElementById('site-cart-sidebar');
      this.overlay = document.querySelector('.site-overlay');
      if (!this.el) return;

      // Wait for the custom element to be defined, then override its methods
      this.waitForCustomElement().then(() => {
        this.overrideSidebarMethods();
      });

      this.bindClose();
      this.bindOverlay();
      this.bindEscape();
    },

    waitForCustomElement() {
      // Wait for sidebar-drawer custom element to be defined
      if (customElements.get('sidebar-drawer')) {
        return Promise.resolve();
      }
      return customElements.whenDefined('sidebar-drawer');
    },

    overrideSidebarMethods() {
      // Take full control of this sidebar's show/hide
      const self = this;

      // Use defineProperty to ensure our methods take precedence
      Object.defineProperty(this.el, 'show', {
        value: function() {
          self.open();
        },
        writable: true,
        configurable: true
      });

      Object.defineProperty(this.el, 'hide', {
        value: function() {
          self.close();
        },
        writable: true,
        configurable: true
      });
    },

    bindClose() {
      this.el.querySelectorAll('[data-js-close]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.close();
        });
      });
    },

    bindOverlay() {
      if (this.overlay) {
        this.overlay.addEventListener('click', () => {
          if (this.isOpen) this.close();
        });
      }
    },

    bindEscape() {
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });
    },

    open() {
      if (this.isOpen) return;

      // Show drawer (off-screen initially due to transform)
      this.el.style.display = 'grid';

      // Force reflow so browser registers the initial transformed state
      this.el.offsetHeight;

      // Add body classes for overlay
      document.body.classList.add('sidebar-opened', 'sidebar-opened--right');

      // Trigger slide animation
      this.el.classList.add('sidebar--opened');
      this.isOpen = true;
      this.el._opened = true;

      // Set inert on background elements
      if (window.inertElems) {
        window.inertElems.forEach(el => el.setAttribute('inert', ''));
      }
    },

    close() {
      if (!this.isOpen) return;

      // Start slide out
      this.el.classList.remove('sidebar--opened');
      this.isOpen = false;
      this.el._opened = false;

      // Remove body classes
      document.body.classList.remove('sidebar-opened', 'sidebar-opened--right', 'sidebar-opened--left');

      // Remove inert
      if (window.inertElems) {
        window.inertElems.forEach(el => el.removeAttribute('inert'));
      }

      // Update aria
      const trigger = document.querySelector(`[aria-controls="${this.el.id}"]`);
      if (trigger) trigger.setAttribute('aria-expanded', 'false');

      // Hide after animation completes
      setTimeout(() => {
        if (!this.isOpen) {
          this.el.style.display = 'none';
        }
      }, 320);
    }
  };

  // ============================================================
  // Cart Controller
  // ============================================================

  const Cart = {
    selectors: {
      drawer: '[data-cart-drawer]',
      loading: '[data-cart-loading]',
      footer: '[data-cart-footer]',
      quantityMinus: '[data-quantity-minus]',
      quantityPlus: '[data-quantity-plus]',
      quantityInput: '[data-quantity-input]',
      removeItem: '[data-remove-item]',
      cartTotal: '[data-cart-total]',
      cartCompare: '[data-cart-compare]',
      cartCount: '[data-cart-count]',
      headerCartCount: '[data-header-cart-count]',
      savingsBanner: '[data-savings-banner]',
      savingsAmount: '[data-savings-amount]'
    },

    init() {
      this.bindEvents();
      this.updateSavings();
    },

    bindEvents() {
      // Quantity and remove buttons
      document.addEventListener('click', (e) => {
        const minus = e.target.closest(this.selectors.quantityMinus);
        const plus = e.target.closest(this.selectors.quantityPlus);
        const remove = e.target.closest(this.selectors.removeItem);

        if (minus) {
          e.preventDefault();
          this.adjustQuantity(minus.dataset.itemKey, -1);
        } else if (plus) {
          e.preventDefault();
          this.adjustQuantity(plus.dataset.itemKey, 1);
        } else if (remove) {
          e.preventDefault();
          this.setQuantity(remove.dataset.itemKey, 0);
        }
      });

      // Direct input changes
      document.addEventListener('change', (e) => {
        const input = e.target.closest(this.selectors.quantityInput);
        if (input) {
          const qty = parseInt(input.value, 10);
          if (!isNaN(qty) && qty >= 0) {
            this.setQuantity(input.dataset.itemKey, qty);
          }
        }
      });

      // Listen for external cart updates (document level)
      document.addEventListener('cart-updated', (e) => {
        if (e.detail) this.onUpdated(e.detail);
      });

      // Listen for cart:refresh events (from gift progress, etc.)
      document.addEventListener('cart:refresh', () => {
        this.refresh();
      });

      // Listen for cart-updated on the cart form element (theme dispatches here too)
      const cartForm = document.getElementById('AjaxCartForm');
      if (cartForm) {
        cartForm.addEventListener('cart-updated', () => {
          this.refresh();
        });
      }
    },

    adjustQuantity(key, delta) {
      const item = document.querySelector(`[data-item-key="${key}"]`);
      if (!item) return;

      const input = item.querySelector(this.selectors.quantityInput);
      if (!input) return;

      const current = parseInt(input.value, 10) || 0;
      const next = Math.max(0, current + delta);
      input.value = next;
      this.setQuantity(key, next);
    },

    setQuantity(key, quantity) {
      this.showLoading();

      fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ id: key, quantity })
      })
        .then(res => res.json())
        .then(cart => {
          this.refresh();
          document.dispatchEvent(new CustomEvent('cart-updated', { detail: cart }));
        })
        .catch(err => {
          console.error('Cart update failed:', err);
          this.hideLoading();
        });
    },

    refresh() {
      fetch(`${window.location.pathname}?section_id=helper-cart`)
        .then(res => res.text())
        .then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html');

          // Update drawer content
          const newDrawer = doc.querySelector('[data-cart-drawer]');
          const drawer = document.querySelector('[data-cart-drawer]');
          if (newDrawer && drawer) {
            drawer.innerHTML = newDrawer.innerHTML;
          }

          // Update footer
          const newFooter = doc.querySelector('[data-cart-footer]');
          const footer = document.querySelector('[data-cart-footer]');
          if (newFooter && footer) {
            footer.outerHTML = newFooter.outerHTML;
          }

          return fetch('/cart.js');
        })
        .then(res => res.json())
        .then(cart => {
          this.updateCount(cart.item_count);
          this.updateEmptyState(cart.item_count === 0);
          this.renderSavings(cart);
          this.hideLoading();
        })
        .catch(err => {
          console.error('Refresh failed:', err);
          this.hideLoading();
        });
    },

    showLoading() {
      const el = document.querySelector(this.selectors.loading);
      if (el) el.classList.add('is-visible');
    },

    hideLoading() {
      const el = document.querySelector(this.selectors.loading);
      if (el) el.classList.remove('is-visible');
    },

    updateCount(count) {
      document.querySelectorAll(this.selectors.headerCartCount).forEach(el => {
        el.textContent = count;
      });
      document.querySelectorAll(this.selectors.cartCount).forEach(el => {
        el.textContent = count;
      });

      const sidebar = document.getElementById('site-cart-sidebar');
      if (sidebar) sidebar.dataset.cartItems = count;
    },

    updateEmptyState(isEmpty) {
      const sidebar = document.getElementById('site-cart-sidebar');
      if (sidebar) sidebar.classList.toggle('cart-is-empty', isEmpty);

      const footer = document.querySelector(this.selectors.footer);
      if (footer) footer.classList.toggle('cart-drawer__footer--hidden', isEmpty);
    },

    updateSavings() {
      fetch('/cart.js')
        .then(res => res.json())
        .then(cart => this.renderSavings(cart))
        .catch(() => {});
    },

    renderSavings(cart) {
      // Use Shopify's cart values directly
      const savings = cart.original_total_price - cart.total_price;
      const banner = document.querySelector(this.selectors.savingsBanner);
      const savingsAmount = document.querySelector(this.selectors.savingsAmount);
      const comparePrice = document.querySelector(this.selectors.cartCompare);
      const subtotalRow = document.querySelector('[data-subtotal-row]');

      // Only show savings if actually saving more than 0 (in cents)
      if (savings > 0 && cart.original_total_price > cart.total_price) {
        // Show savings elements
        if (banner) {
          banner.classList.remove('hidden');
          if (savingsAmount) {
            savingsAmount.textContent = '- ' + this.formatMoney(savings);
          }
        }
        if (comparePrice) {
          comparePrice.classList.remove('hidden');
          comparePrice.textContent = '(' + this.formatMoney(cart.original_total_price) + ')';
        }
        if (subtotalRow) {
          subtotalRow.classList.add('cart-drawer__subtotal-row--has-savings');
        }
      } else {
        // Hide savings elements
        if (banner) {
          banner.classList.add('hidden');
        }
        if (comparePrice) {
          comparePrice.classList.add('hidden');
        }
        if (subtotalRow) {
          subtotalRow.classList.remove('cart-drawer__subtotal-row--has-savings');
        }
      }
    },

    formatMoney(cents) {
      const format = window.KROWN?.settings?.shop_money_format || '{{amount_with_comma_separator}} €';
      const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
      if (!match) return (cents / 100).toFixed(2);

      const num = cents / 100;
      let value;

      switch (match[1]) {
        case 'amount':
        case 'amount_with_comma_separator':
          value = num.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
          break;
        case 'amount_no_decimals':
        case 'amount_no_decimals_with_comma_separator':
          value = Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
          break;
        default:
          value = num.toFixed(2).replace('.', ',');
      }

      return format.replace(/\{\{\s*\w+\s*\}\}/, value);
    },

    onUpdated(cart) {
      this.updateCount(cart.item_count);
      this.renderSavings(cart);
      this.updateEmptyState(cart.item_count === 0);

      const total = document.querySelector(this.selectors.cartTotal);
      if (total) total.textContent = this.formatMoney(cart.total_price);
    }
  };

  // ============================================================
  // Initialize
  // ============================================================

  function init() {
    Drawer.init();
    Cart.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for external use
  window.CartDrawer = { Drawer, Cart };

})();
